/**
 * M-1 PR-d: AuthBridge para apps/crm.
 *
 * Substitui o antigo AuthContext.shim.tsx (blue-green) e o AuthProvider
 * legado em AuthContext.tsx. Agora é o único Provider — sem feature flag
 * VITE_USE_PPS_AUTH nem fallback ao legado.
 *
 * Renderiza `<AuthProvider>` de @pps/auth-client com features
 * `broadcastSignOut` + `visibilityRehydrate` + `guardSpuriousSignOut` +
 * `multiClub`, e o `LegacyContextBridge` republica a shape completa
 * (staffRole + viewScope + networkClubs + networkClubIds + isNetworkAdmin)
 * no Context exportado por `./AuthContext.tsx`, mantendo intactos os 66
 * call sites de `useAuth()`.
 *
 * Comportamentos do legado preservados:
 *   1) Sentry.setUser nas transições SIGNED_IN/SIGNED_OUT (via onAuthChange).
 *   2) selectClub persistido em `localStorage.selected_club_id`, com
 *      validação server-side (descarta UUID que não pertence à lista).
 *   3) staffRole derivado via RPC `get_my_club_role(_club_id)` ao mudar club.
 *   4) networkClubs via SELECT pai+filhos do clube atual (cache 60s).
 *   5) isNetworkAdmin via RPC `is_network_admin(_user_id, _club_id)`.
 *   6) viewScope state machine: restaura/valida persistido, força `club` se
 *      não-admin sem rede, default network para admin / club caso contrário.
 *   7) signOut limpa `selected_club_id` + `viewScope:<userId>`.
 *   8) signIn/signUp legados fazem `throw` em erro — bridge converte do
 *      `{error}` shape do package.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import * as Sentry from "@sentry/react";
import { toSentryError } from "@pps/observability";
import { useQuery } from "@tanstack/react-query";
import { qk } from "@pps/query-keys";
import {
  AuthProvider as PpsAuthProvider,
  isImpersonationTab,
  useAuth as usePpsAuth,
  type MultiClubLoader,
  type OnAuthChange,
  type ProfileLoader,
} from "@pps/auth-client";
import { supabase } from "@/integrations/supabase/client";
import {
  AuthContext,
  type AuthContextType,
  type Club,
  type Profile,
} from "./AuthContext";
import {
  isViewScope,
  viewScopeStorageKey,
  type ViewScope,
} from "@/types/viewScope";

/**
 * Profile loader plugado no @pps/auth-client. SELECT idêntico ao legado.
 * Sem i18n sync aqui (o CRM legado não fazia i18n.changeLanguage no
 * AuthProvider — i18n é controlado em outras camadas).
 */
const loadProfile: ProfileLoader<Profile> = async (client, userId) => {
  const { data, error } = await client
    .from("profiles")
    .select("*")
    .eq("user_id", userId)
    .single();
  if (error || !data) return null;
  return data as Profile;
};

/**
 * MultiClub loader: replica o merge owned+staff do AuthContext.tsx legado
 * (`fetchUserData`) e respeita `localStorage.selected_club_id` para o
 * `initial`, com validação server-side (UUID precisa pertencer à lista
 * carregada — defesa contra manipulação via DevTools).
 */
const loadMultiClub: MultiClubLoader<Club> = async (client, userId) => {
  // ORDER BY created_at: sem ele o `initial = allClubs[0]` (fallback quando
  // não há selected_club_id) segue a ordem física do heap — um clube demo
  // criado depois pode "roubar" o slot do clube original do dono.
  const [ownedClubsRes, staffRes] = await Promise.all([
    client
      .from("clubs")
      .select("*")
      .eq("owner_id", userId)
      .eq("is_active", true)
      .order("created_at", { ascending: true }),
    client
      .from("club_staff")
      .select("club_id, role")
      .eq("user_id", userId)
      .eq("is_active", true),
  ]);

  const ownedClubs = (ownedClubsRes.data ?? []) as Club[];
  const ownedIds = new Set(ownedClubs.map((c) => c.id));

  const staffClubIds = (staffRes.data ?? [])
    .map((s) => s.club_id as string)
    .filter((id) => !ownedIds.has(id));

  let staffClubs: Club[] = [];
  if (staffClubIds.length > 0) {
    const { data } = await client
      .from("clubs")
      .select("*")
      .in("id", staffClubIds)
      .eq("is_active", true)
      .order("created_at", { ascending: true });
    staffClubs = (data ?? []) as Club[];
  }

  const allClubs = [...ownedClubs, ...staffClubs];
  if (allClubs.length === 0) return { clubs: [], initial: null };

  const savedClubId = typeof window !== "undefined"
    ? localStorage.getItem("selected_club_id")
    : null;
  const savedClub = savedClubId
    ? allClubs.find((c) => c.id === savedClubId) ?? null
    : null;
  if (savedClubId && !savedClub && typeof window !== "undefined") {
    // savedClubId não pertence a este user — descartar
    localStorage.removeItem("selected_club_id");
  }
  const initial = savedClub ?? allClubs[0];
  return { clubs: allClubs, initial };
};

/**
 * Callback `onAuthChange`: Sentry.setUser + registro de login (paridade com
 * o app público — Saúde dos Clubes F1). Historicamente o CRM não gravava
 * last_login_at nem activity log, e o monitoramento de uso por clube ficava
 * cego exatamente para o app onde o dono/staff vive. Fire-and-forget: falha
 * aqui nunca pode atrapalhar o login.
 */
const onAuthChange: OnAuthChange = (event, session) => {
  if (session?.user) {
    Sentry.setUser({
      id: session.user.id,
      email: session.user.email ?? undefined,
    });
  } else {
    Sentry.setUser(null);
  }

  // Aba de suporte (impersonação): o login é do ADMIN, não do usuário. Não
  // grava último acesso nem log de login — senão a saúde do clube e o
  // "último acesso" do owner mentem. A auditoria da sessão de suporte fica
  // em impersonation_sessions + admin_audit_logs.
  if (event === "SIGNED_IN" && session?.user && !isImpersonationTab()) {
    void supabase
      .from("profiles")
      .update({ last_login_at: new Date().toISOString() })
      .eq("user_id", session.user.id)
      .then(() => {});
    void supabase
      .from("user_activity_logs")
      .insert({ user_id: session.user.id, activity_type: "login" })
      .then(() => {});
  }
};

/**
 * Bridge: consome `useAuth` de @pps/auth-client e republica no Context
 * legado a shape completa do CRM, re-implementando staffRole + network +
 * viewScope como no AuthContext.tsx legado.
 */
function LegacyContextBridge({ children }: { children: ReactNode }): ReactElement {
  const ctx = usePpsAuth<Profile, Club>();
  const { user, session, profile, loading, multiClub } = ctx;
  const club: Club | null = multiClub?.club ?? null;
  const clubs: Club[] = multiClub?.clubs ?? [];

  // staffRole derivado via RPC quando muda o club selecionado.
  const [staffRole, setStaffRole] = useState<string | null>(null);
  useEffect(() => {
    if (!user || !club) {
      setStaffRole(null);
      return;
    }
    let cancelled = false;
    void supabase
      .rpc("get_my_club_role", { _club_id: club.id })
      .then(({ data }) => {
        if (!cancelled) setStaffRole((data as string | null) ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [user, club]);

  // --- Rede multi-clube (A.1) — copiado do legado, mesma semântica. -------
  const networkParentId: string | null = useMemo(() => {
    if (!club) return null;
    return club.parent_club_id ?? club.id;
  }, [club]);

  const networkQuery = useQuery({
    queryKey: ["auth", "network-clubs", club?.id ?? null],
    enabled: !!club,
    staleTime: 60_000,
    queryFn: async (): Promise<Club[]> => {
      if (!club) return [];
      const orParts = [`id.eq.${club.id}`, `parent_club_id.eq.${club.id}`];
      if (club.parent_club_id) orParts.push(`id.eq.${club.parent_club_id}`);
      const { data, error } = await supabase
        .from("clubs")
        .select("*")
        .eq("is_active", true)
        .or(orParts.join(","));
      if (error) throw error;
      return (data ?? []) as Club[];
    },
  });

  const networkClubs: Club[] = useMemo(
    () => networkQuery.data ?? [],
    [networkQuery.data]
  );
  const networkClubIds: string[] = useMemo(() => {
    if (networkClubs.length > 0) return networkClubs.map((c) => c.id);
    return club ? [club.id] : [];
  }, [networkClubs, club]);

  const networkAdminQuery = useQuery({
    queryKey: qk.auth.isNetworkAdmin(user?.id, club?.id),
    enabled: !!user && !!club,
    staleTime: 60_000,
    queryFn: async (): Promise<boolean> => {
      if (!user || !club) return false;
      const { data, error } = await supabase.rpc("is_network_admin", {
        _user_id: user.id,
        _club_id: club.id,
      });
      if (error) {
        Sentry.captureException(toSentryError(error), { tags: { context: "is_network_admin" } });
        return false;
      }
      return data === true;
    },
  });

  const isNetworkAdmin: boolean = networkAdminQuery.data ?? false;

  // viewScope state machine — copiada do legado
  const [viewScopeState, setViewScopeState] = useState<ViewScope | null>(null);

  useEffect(() => {
    if (!user || !club) {
      setViewScopeState(null);
      return;
    }
    if (networkQuery.isLoading || networkAdminQuery.isLoading) return;

    const storageKey = viewScopeStorageKey(user.id);
    const rawStored = localStorage.getItem(storageKey);
    let restored: ViewScope | null = null;
    if (rawStored) {
      try {
        const parsed: unknown = JSON.parse(rawStored);
        if (isViewScope(parsed)) restored = parsed;
      } catch {
        // JSON inválido — descartar silenciosamente.
      }
    }

    const validIds = new Set(networkClubIds);
    const expectedParentId = networkParentId;
    if (restored) {
      if (restored.type === "club" && !validIds.has(restored.clubId)) {
        restored = null;
      } else if (
        restored &&
        restored.type === "network" &&
        expectedParentId &&
        restored.parentId !== expectedParentId
      ) {
        restored = null;
      }
    }

    const hasNetwork = networkClubs.length > 1;
    if (!isNetworkAdmin && !hasNetwork) {
      const forced: ViewScope = { type: "club", clubId: club.id };
      setViewScopeState(forced);
      localStorage.setItem(storageKey, JSON.stringify(forced));
      return;
    }

    if (restored) {
      if (typeof window !== "undefined" && window.DEBUG_VIEWSCOPE) {
        // eslint-disable-next-line no-console
        console.log("[viewScope][bridge] effect restored from localStorage", {
          restored,
          networkClubIdsLen: networkClubIds.length,
          isNetworkAdmin,
        });
      }
      setViewScopeState(restored);
      return;
    }

    const fallbackParent = club.parent_club_id ?? club.id;
    const defaultScope: ViewScope = isNetworkAdmin
      ? { type: "network", parentId: fallbackParent }
      : { type: "club", clubId: club.id };
    if (typeof window !== "undefined" && window.DEBUG_VIEWSCOPE) {
      // eslint-disable-next-line no-console
      console.log("[viewScope][bridge] effect set default scope", {
        defaultScope,
        isNetworkAdmin,
        networkClubsLen: networkClubs.length,
      });
    }
    setViewScopeState(defaultScope);
    localStorage.setItem(storageKey, JSON.stringify(defaultScope));
  }, [
    user,
    club,
    isNetworkAdmin,
    networkClubs,
    networkClubIds,
    networkParentId,
    networkQuery.isLoading,
    networkAdminQuery.isLoading,
  ]);

  // DIAG TEMP (fix/viewscope-diag-logs): log gated por window.DEBUG_VIEWSCOPE
  // para diagnosticar follow-up do PR #192. Remover após confirmar root cause.
  const debugViewScope = (label: string, payload: unknown): void => {
    if (typeof window !== "undefined" && window.DEBUG_VIEWSCOPE) {
      // eslint-disable-next-line no-console
      console.log(`[viewScope][bridge] ${label}`, payload);
    }
  };

  // DIAG TEMP (fix/viewscope-bridge-propagation): canary log no render do Bridge.
  // Confirma que o componente realmente re-renderiza após setViewScopeState.
  if (typeof window !== "undefined" && window.DEBUG_VIEWSCOPE) {
    // eslint-disable-next-line no-console
    console.log("[viewScope][bridge-render]", {
      viewScopeState,
      hasUser: !!user,
      hasClub: !!club,
      networkClubsLen: networkClubs.length,
      networkClubIdsLen: networkClubIds.length,
      isNetworkAdmin,
    });
  }

  // DIAG TEMP: confirma que viewScopeState aplicou após setState.
  useEffect(() => {
    if (typeof window !== "undefined" && window.DEBUG_VIEWSCOPE) {
      // eslint-disable-next-line no-console
      console.log("[viewScope][bridge-state-effect] viewScopeState changed", { viewScopeState });
    }
  }, [viewScopeState]);

  const setViewScope = useCallback(
    (next: ViewScope): void => {
      if (!user) {
        debugViewScope("setViewScope REJECTED (no user)", next);
        return;
      }
      const hasNetwork = networkClubs.length > 1;
      if (next.type === "network" && !isNetworkAdmin && !hasNetwork) {
        debugViewScope("setViewScope REJECTED (network mode but !admin && !hasNetwork)", {
          next,
          isNetworkAdmin,
          networkClubsLength: networkClubs.length,
        });
        return;
      }
      if (next.type === "club" && !networkClubIds.includes(next.clubId)) {
        debugViewScope("setViewScope REJECTED (clubId not in networkClubIds)", {
          next,
          networkClubIds,
        });
        return;
      }
      debugViewScope("setViewScope ACCEPTED → setState + localStorage", { next });
      setViewScopeState(next);
      localStorage.setItem(viewScopeStorageKey(user.id), JSON.stringify(next));
    },
    [user, networkClubs, networkClubIds, isNetworkAdmin]
  );

  // Memoizado para garantir identidade estável quando viewScopeState não muda —
  // evita value useMemo recomputar à toa em re-renders da Bridge causados por
  // staffRole/networkClubs/etc. e mantém referência nova quando deveria mudar.
  const effectiveViewScope: ViewScope = useMemo<ViewScope>(() => {
    if (viewScopeState) return viewScopeState;
    if (club) return { type: "club", clubId: club.id };
    return { type: "club", clubId: "" };
  }, [viewScopeState, club]);

  // selectClub: delega ao multiClub do package + persiste em localStorage,
  // além de pré-carregar o staffRole para reduzir flash (mesmo padrão do
  // legado, que faz fire-and-forget). O staffRole também é coberto pelo
  // useEffect acima, mas o set imediato evita 1 frame com role stale.
  const selectClub = useCallback(
    (c: Club): void => {
      multiClub?.selectClub(c);
      try {
        localStorage.setItem("selected_club_id", c.id);
      } catch {
        // localStorage indisponível (modo privado em algum browser) — ignora.
      }
      if (user) {
        void supabase
          .rpc("get_my_club_role", { _club_id: c.id })
          .then(({ data }) => setStaffRole((data as string | null) ?? null));
      }
    },
    [multiClub, user]
  );

  // Bridge das ações: legado faz throw em erro (signIn/signUp/signOut) e
  // signOut limpa localStorage. Replicar exatamente.
  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      const { error } = await ctx.signIn(email, password);
      if (error) throw error;
    },
    [ctx]
  );

  const signUp = useCallback(
    async (email: string, password: string, fullName: string): Promise<void> => {
      const redirectTo =
        typeof window !== "undefined" ? window.location.origin : undefined;
      const { error } = await ctx.signUp(email, password, { fullName, redirectTo });
      if (error) throw error;
    },
    [ctx]
  );

  const signOut = useCallback(async (): Promise<void> => {
    const currentUserId = user?.id;
    await ctx.signOut();
    Sentry.setUser(null);
    try {
      localStorage.removeItem("selected_club_id");
      if (currentUserId) {
        localStorage.removeItem(viewScopeStorageKey(currentUserId));
      }
    } catch {
      // localStorage indisponível — não bloquear sign-out por causa disso.
    }
  }, [ctx, user?.id]);

  const value = useMemo<AuthContextType>(
    () => ({
      session,
      user,
      profile,
      club,
      clubs,
      staffRole,
      loading,
      signIn,
      signUp,
      signOut,
      selectClub,
      viewScope: effectiveViewScope,
      setViewScope,
      networkClubs,
      networkClubIds,
      isNetworkAdmin,
      // Sem republicar, editar nome/foto em /minha-conta não atualizaria o
      // SidebarFooter até um reload — o provider já expõe.
      refreshProfile: ctx.refreshProfile,
    }),
    [
      session,
      user,
      profile,
      club,
      clubs,
      staffRole,
      loading,
      signIn,
      signUp,
      signOut,
      selectClub,
      effectiveViewScope,
      setViewScope,
      networkClubs,
      networkClubIds,
      isNetworkAdmin,
      ctx.refreshProfile,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Provider público. App.tsx importa `AuthProvider` daqui em vez do antigo
 * shim/legado. Único caminho — sem flag, sem branch.
 */
export function AuthProvider({ children }: { children: ReactNode }): ReactElement {
  return (
    <PpsAuthProvider<Profile, Club>
      supabaseClient={supabase}
      profileLoader={loadProfile}
      multiClubLoader={loadMultiClub}
      features={{
        // Aba de suporte não participa do canal "pps-auth": o signOut dela
        // não pode derrubar as outras abas do origin, e o das outras não
        // pode derrubar a aba de suporte.
        broadcastSignOut: !isImpersonationTab(),
        visibilityRehydrate: true,
        guardSpuriousSignOut: true,
        multiClub: true,
      }}
      onAuthChange={onAuthChange}
    >
      <LegacyContextBridge>{children}</LegacyContextBridge>
    </PpsAuthProvider>
  );
}
